import { useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  Field,
  Spinner,
  Textarea,
  Button,
  Input,
  makeStyles,
  shorthands,
  tokens,
  DrawerFooter,
} from "@fluentui/react-components";
import { Dismiss24Regular, Search20Regular } from "@fluentui/react-icons";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import ErrorMessageBar from "../../../shared/components/ErrorMessageBar";
import { useNotify } from "../../../shared/components/SuperToast";
import { GenericComboBox } from "../../../shared/components/GenericComboBox";
import CiSelector from "../../ci/components/CiSelector";

import "../../../styles/animations.css";

// Business logic
import { useSelectedCiRelationsContext } from "../context/SelectedCiRelationsContext";
import { useCiRelationQuery } from "../hooks/useCiRelationQueries";
import { useCreateCiRelation, useUpdateCiRelation } from "../hooks/useCiRelationMutations";
import { ciRelationSchema } from "@shared/schemas";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { readCis } from "../../ci/services/ciApi";
import { readCiRelationTypes } from "../../ciRelationTypes/services/ciRelationTypesApi";

const useStyles = makeStyles({
  root: {
    overflow: "visible",
    display: "flex",
    backgroundColor: "#fff",
    zIndex: "1000",
  },
  content: {
    flex: "1",
    padding: "16px",
    display: "grid",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gridRowGap: tokens.spacingVerticalXXL,
    gridAutoRows: "max-content",
  },
  field: {
    display: "grid",
    gridRowGap: "4px",
    margin: "6px 0",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap("1rem"),
  },
});

function CiRelationDrawer({ isOpen, setIsOpen, newItem, fixedSourceId, fixedTargetId, sourceQuery }) {
  const { t } = useTranslation();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [editingItem, setEditingItem] = useState(null);
  const [isSourceSelectorOpen, setIsSourceSelectorOpen] = useState(false);
  const [isTargetSelectorOpen, setIsTargetSelectorOpen] = useState(false);
  const { value: selectedItems } = useSelectedCiRelationsContext();
  const { notify } = useNotify();
  const drawerRef = useRef(null);

  // Restore focus to drawer when selectors close
  useEffect(() => {
    if (!isSourceSelectorOpen && !isTargetSelectorOpen && isOpen && drawerRef.current) {
      // Small timeout to ensure dialog has fully closed
      setTimeout(() => {
        drawerRef.current?.focus();
      }, 100);
    }
  }, [isSourceSelectorOpen, isTargetSelectorOpen, isOpen]);

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateItem = useItemValidation(ciRelationSchema.$defs.ciRelation);

  const publicId = selectedItems?.[0]?.public_id ?? null;

  // Fetch reference data
  const { data: cisData = [] } = useItemQuery(["cis"], () => readCis({}), true);
  const { data: relationTypesData = [] } = useItemQuery(["ci_relation_types"], () => readCiRelationTypes(), true);

  const isSourceFixed = typeof fixedSourceId === "number" && !isNaN(fixedSourceId);
  const isTargetFixed = typeof fixedTargetId === "number" && !isNaN(fixedTargetId);

  // 1. Query and mutations
  const { data, error, isError, isLoading, isSuccess } = useCiRelationQuery(publicId, isOpen && !newItem);

  const {
    mutate: createCiRelation,
    isPending: isCreating,
    error: errorCreating,
    reset: resetCreating,
  } = useCreateCiRelation(
    ["ci_relations"],
    (data) => {
      queryClient.invalidateQueries({ queryKey: sourceQuery || ["ci_relations"] });
      notify(t("item.messages.created", { name: `Relation #${data.id}` }), "success");
      handleClose();
    },
    null,
    false,
  );

  const {
    mutate: updateCiRelation,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useUpdateCiRelation(
    ["ci_relations"],
    (data) => {
      queryClient.invalidateQueries({ queryKey: sourceQuery || ["ci_relations"] });
      notify(t("item.messages.saved", { name: `Relation #${data.id}` }), "success");
      handleClose();
    },
    null,
  );

  // 2. Effects
  useEffect(() => {
    if (!isOpen) return;

    if (newItem) {
      const initialItem = {
        id: 0,
        source_id: isSourceFixed ? fixedSourceId : null,
        target_id: isTargetFixed ? fixedTargetId : null,
        relation_type_id: null,
        comments: "",
      };
      setEditingItem(initialItem);
      setValidationErrors(validateItem(initialItem));
    } else if (data && isSuccess) {
      setEditingItem(data);
    } else setEditingItem(null);
  }, [isOpen, data, newItem, isSuccess, isSourceFixed, fixedSourceId, isTargetFixed, fixedTargetId]);

  useEffect(() => {
    if (isOpen && isError) {
      notify(error.message, "error");
      handleClose();
    }
  }, [isOpen, isError]);

  // 3. Handlers
  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["ci_relation", publicId] });
    setEditingItem(null);
    setValidationErrors({});
    resetCreating();
    resetUpdating();
    setIsOpen(false);
  };

  const handleOpenChange = (_, data) => {
    if (data.type !== "backdropClick") setIsOpen(data.open);
  };

  const updateField = (field, value) => {
    const updated = { ...editingItem, [field]: value };
    setEditingItem(updated);
    setValidationErrors(validateItem(updated));
  };

  // Get display names for drawer title
  const [sourceCi, setSourceCi] = useState(null);
  const [targetCi, setTargetCi] = useState(null);
  const relationType = relationTypesData.find((rt) => rt.id === editingItem?.relation_type_id);

  // Update CI display names when IDs change
  useEffect(() => {
    if (editingItem?.source_id) {
      const ci = cisData.find((ci) => ci.id === editingItem.source_id);
      setSourceCi(ci || null);
    } else {
      setSourceCi(null);
    }
  }, [editingItem?.source_id, cisData]);

  useEffect(() => {
    if (editingItem?.target_id) {
      const ci = cisData.find((ci) => ci.id === editingItem.target_id);
      setTargetCi(ci || null);
    } else {
      setTargetCi(null);
    }
  }, [editingItem?.target_id, cisData]);

  const handleSourceCiSelect = (ci) => {
    if (isSourceFixed) return;
    updateField("source_id", ci.id);
    setSourceCi(ci);
  };

  const handleTargetCiSelect = (ci) => {
    if (isTargetFixed) return;
    updateField("target_id", ci.id);
    setTargetCi(ci);
  };

  const drawerTitle = newItem
    ? t("ciRelation.new", { defaultValue: "New CI Relation" })
    : sourceCi && targetCi && relationType
      ? `${sourceCi.name} ${relationType.name} ${targetCi.name}`
      : `Relation #${editingItem?.id || ""}`;

  return (
    <div className={styles.root}>
      <Drawer type="overlay" position="end" separator size="medium" open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={() => setIsOpen(false)}
              />
            }
          >
            {drawerTitle}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <hr />
        {!newItem && isLoading && (
          <div
            className="blur-overlay"
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Spinner size="medium" />
          </div>
        )}
        <DrawerBody ref={drawerRef} tabIndex={-1}>
          <Field
            className={styles.field}
            label={t("ciRelation.sourceCI", { defaultValue: "Source CI" })}
            validationState={validationErrors.source_id ? "error" : undefined}
            required
          >
            <Input
              contentAfter={<Search20Regular />}
              value={sourceCi?.name || ""}
              placeholder={t("ciRelation.selectSourceCI", { defaultValue: "Select source CI" })}
              readOnly
              disabled={isSourceFixed}
              onClick={() => !isSourceFixed && setIsSourceSelectorOpen(true)}
              style={{ cursor: "pointer" }}
            />
          </Field>

          <Field
            className={styles.field}
            label={t("ciRelation.relationType", { defaultValue: "Relation Type" })}
            validationState={validationErrors.relation_type_id ? "error" : undefined}
            required
          >
            <GenericComboBox
              items={relationTypesData}
              value={editingItem?.relation_type_id}
              valueKey="id"
              displayKey={(item) => `${item.name} / ${item.reverse_name}`}
              itemTemplate={(item) => `${item.name} / ${item.reverse_name}`}
              placeholder={t("ciRelation.selectRelationType", { defaultValue: "Select relation type" })}
              onOptionSelect={(value) => updateField("relation_type_id", value)}
            />
          </Field>

          <Field
            className={styles.field}
            label={t("ciRelation.targetCI", { defaultValue: "Target CI" })}
            validationState={validationErrors.target_id ? "error" : undefined}
            required
          >
            <Input
              contentAfter={<Search20Regular />}
              value={targetCi?.name || ""}
              placeholder={t("ciRelation.selectTargetCI", { defaultValue: "Select target CI" })}
              readOnly
              disabled={isTargetFixed}
              onClick={() => !isTargetFixed && setIsTargetSelectorOpen(true)}
              style={{ cursor: "pointer" }}
            />
          </Field>

          <Field
            className={styles.field}
            label={t("generic.comments", { defaultValue: "Comments" })}
            validationState={validationErrors.comments ? "error" : undefined}
          >
            <Textarea
              id="comments"
              maxLength={255}
              value={editingItem?.comments ?? ""}
              onChange={(e) => updateField("comments", e.target.value)}
            />
          </Field>

          {validationErrors && Object.keys(validationErrors).length > 0 && (
            <DataValidationMessageBar validationErrors={validationErrors} />
          )}
          {errorCreating && <ErrorMessageBar error={errorCreating} />}
          {errorUpdating && <ErrorMessageBar error={errorUpdating} />}
        </DrawerBody>
        <DrawerFooter>
          <div className={styles.footerButtons}>
            <Button
              icon={isCreating || isUpdating ? <Spinner size="tiny" /> : undefined}
              appearance="primary"
              disabled={isCreating || isUpdating || Object.keys(validationErrors).length > 0}
              onClick={() => (newItem ? createCiRelation(editingItem) : updateCiRelation(editingItem))}
            >
              {newItem ? t("generic.create") : t("generic.update")}
            </Button>
            <Button onClick={handleClose}>{t("generic.cancel")}</Button>
          </div>
        </DrawerFooter>
      </Drawer>
      {!isSourceFixed && (
        <CiSelector
          isOpen={isSourceSelectorOpen}
          onClose={() => setIsSourceSelectorOpen(false)}
          onSelect={handleSourceCiSelect}
          title={t("ciRelation.selectSourceCI", { defaultValue: "Select Source CI" })}
        />
      )}
      {!isTargetFixed && (
        <CiSelector
          isOpen={isTargetSelectorOpen}
          onClose={() => setIsTargetSelectorOpen(false)}
          onSelect={handleTargetCiSelect}
          title={t("ciRelation.selectTargetCI", { defaultValue: "Select Target CI" })}
        />
      )}
    </div>
  );
}

export default CiRelationDrawer;
